import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, MessageCircle } from "lucide-react";
import Sidebar from "./Sidebar";
import ContextPanel from "./ContextPanel";
import { cn } from "@/lib/utils";

const EASE_SMOOTH = [0.2, 0.8, 0.2, 1];

/**
 * MainLayout — shell que wrappar todo o NectarDashboard e SettingsPage.
 *
 * Desktop (≥768px):
 *   - Sidebar permanente esquerda (variant="permanent")
 *   - ContextPanel sidebar direita (variant="permanent")
 *   - Main content flutua entre os dois
 *
 * Mobile (<768px):
 *   - MobileTopBar fixed top (hamburger + logo + chat trigger)
 *   - Sidebar drawer (variant="drawer") aciona via hamburger
 *   - ContextPanel bottom sheet (variant="bottom-sheet" — chega Etapa 2)
 *   - Sem bottom nav (era 4 atalhos; agora drawer com 9 sections)
 *
 * Mobile Parity Epic — Etapa 1.
 */
export default function MainLayout({
  children,
  activeSection = "home",
  onSectionChange,
  showContextPanel = true,
  contextPanelContent,
  confirmations = [],
  messages = [],
  isFocusMode = false,
  isConversationMode = false,
  onNewChat,
  chatInput = null,  // Mobile Parity Etapa 4: ChatInputBar passado via prop
}) {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [contextPanelOpen, setContextPanelOpen] = useState(true);

  // Mobile parity Epic Etapa 1/2: estados pros drawer/bottom-sheet mobile.
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const [contextPanelMobileOpen, setContextPanelMobileOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const sidebarWidth = sidebarExpanded ? 240 : 72;
  const contextPanelWidth = contextPanelOpen && showContextPanel && !isFocusMode ? 384 : 0;

  return (
    <div className="min-h-screen bg-surface-raised text-foreground overflow-hidden">
      {/* Conversation Mode Overlay */}
      <AnimatePresence>
        {isConversationMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE_SMOOTH }}
            className="fixed inset-0 bg-surface-canvas/40 backdrop-blur-sm z-20"
            data-testid="conversation-overlay"
          />
        )}
      </AnimatePresence>

      {/* Sidebar desktop — INTOCADO */}
      {!isFocusMode && !isMobile && (
        <Sidebar
          activeSection={activeSection}
          onSectionChange={onSectionChange}
          isExpanded={sidebarExpanded}
          onToggleExpand={() => setSidebarExpanded(!sidebarExpanded)}
          variant="permanent"
        />
      )}

      {/* Mobile Parity Etapa 1: Sidebar drawer mobile. */}
      {isMobile && !isFocusMode && (
        <Sidebar
          activeSection={activeSection}
          onSectionChange={(s) => {
            onSectionChange(s);
            setSidebarDrawerOpen(false);
          }}
          variant="drawer"
          isDrawerOpen={sidebarDrawerOpen}
          onDrawerClose={() => setSidebarDrawerOpen(false)}
        />
      )}

      {/* Mobile Parity Etapa 1: TopBar fixed mobile (hamburger + logo + chat trigger). */}
      {isMobile && !isFocusMode && !isConversationMode && (
        <div
          className="fixed top-0 left-0 right-0 h-12 bg-surface-card/95 backdrop-blur-md border-b border-subtle z-30 flex items-center justify-between px-4 safe-area-pt"
          data-testid="mobile-top-bar"
        >
          <button
            type="button"
            onClick={() => setSidebarDrawerOpen(true)}
            aria-label="Abrir menu"
            className="p-2 -ml-2 rounded-lg hover:bg-white/5 text-foreground"
            data-testid="mobile-menu-trigger"
          >
            <Menu className="w-6 h-6" />
          </button>

          <img
            src="/logo-nav.png"
            alt="Nexo"
            className="w-8 h-8 rounded-md object-cover border border-brand-cyan/30"
          />

          <button
            type="button"
            onClick={() => setContextPanelMobileOpen(true)}
            aria-label="Abrir Painel Inteligente"
            className="p-2 -mr-2 rounded-lg hover:bg-white/5 text-foreground relative"
            data-testid="mobile-chat-trigger"
          >
            <MessageCircle className="w-6 h-6" />
            {messages && messages.length > 0 && !contextPanelMobileOpen && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-brand-cyan" />
            )}
          </button>
        </div>
      )}

      {/* Main Content */}
      <motion.main
        animate={{
          marginLeft: isFocusMode || isMobile ? 0 : sidebarWidth,
          marginRight: isFocusMode || isMobile ? 0 : (contextPanelOpen && showContextPanel ? contextPanelWidth : 0),
          paddingTop: isMobile && !isFocusMode && !isConversationMode ? 48 : 0,
        }}
        transition={{ duration: 0.2, ease: EASE_SMOOTH }}
        className={cn("min-h-screen", isConversationMode && "z-30 relative")}
        data-testid="main-content"
      >
        {children}
      </motion.main>

      {/* Context Panel desktop — INTOCADO */}
      <AnimatePresence>
        {showContextPanel && !isFocusMode && !isMobile && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE_SMOOTH }}
            className="fixed right-0 top-0 h-screen"
          >
            <ContextPanel
              isOpen={contextPanelOpen}
              onClose={() => setContextPanelOpen(false)}
              onNewChat={onNewChat}
              confirmations={confirmations}
              messages={messages}
              chatInput={chatInput}
            >
              {contextPanelContent}
            </ContextPanel>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Parity Etapa 2: ContextPanel bottom-sheet mobile. */}
      {showContextPanel && !isFocusMode && isMobile && (
        <ContextPanel
          isOpen={contextPanelMobileOpen}
          onClose={() => setContextPanelMobileOpen(false)}
          onNewChat={onNewChat}
          confirmations={confirmations}
          messages={messages}
          variant="bottom-sheet"
          chatInput={chatInput}
        >
          {contextPanelContent}
        </ContextPanel>
      )}

      {/* Mobile Parity Etapa 1: bottom nav DELETADA. Sino flutuante (FaseC C1)
          removido. */}
    </div>
  );
}
